import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, StyledEngineProvider } from '@mui/material';
import { LicenseInfo } from '@mui/x-license-pro';

import AppRouting from '@/routing/AppRouting';
import store from '@/data/store';
import { GoogleMapsContextProvider } from '@/contexts/GoogleMapsProvider';
import { WebSocketProvider } from '@/contexts/WebSocketProvider';
import { getTheme } from '@/theme';
import { usePortalName } from '@/hooks/usePortalName';
import { configurePortalLabelUserAccessor } from '@/hooks/usePortalLabel';
import { selectLoggedInUserInfo } from '@/data/user.slice';
import { PortalProvider } from '@/contexts/PortalProvider';
import CacheVersionUpdateDialog from '@/components/CacheVersionUpdateDialog';
import PopupLogin from '@/containers/auth/PopupLogin';
import { POPUP_LOGIN_PATH } from '@/services/authService';

// Wire the non-React `getPortalLabel()` (used by httpService and slice thunks)
// to read the logged-in user from the redux store. Done here — not inside
// `usePortalLabel.ts` — to avoid a circular import via the slice graph.
configurePortalLabelUserAccessor(() =>
  selectLoggedInUserInfo(store.getState()),
);

const { __environment__ } = window as Window & {
  __environment__: {
    REACT_APP_DATA_GRID_PRO_KEY: string;
  };
};
const dataGripProKey = __environment__.REACT_APP_DATA_GRID_PRO_KEY;

LicenseInfo.setLicenseKey(dataGripProKey);

const App = () => {
  const portalName = usePortalName();
  const theme = getTheme(portalName);

  // The login popup's bootstrap page must start its redirect immediately, so it
  // is rendered ahead of the router and the redux providers — AppRouting blocks
  // on a user-info fetch before it renders any route.
  if (window.location.pathname === POPUP_LOGIN_PATH) {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <PopupLogin />
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Provider store={store}>
            <GoogleMapsContextProvider>
              <WebSocketProvider>
                <PortalProvider>
                  <AppRouting />
                  <CacheVersionUpdateDialog />
                </PortalProvider>
              </WebSocketProvider>
            </GoogleMapsContextProvider>
          </Provider>
        </BrowserRouter>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
